/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    object, bool, string, func,
} from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter, Redirect } from 'react-router';
import { useQuery } from '@apollo/react-hooks';
import  { compose } from 'recompose';
import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';
import loadable from '@loadable/component';

import GraphqlSearchContainer from '../GraphqlSearchPage/GraphqlSearchContainer';
import CoreBrowsingRedirectContainer from './CoreBrowsingRedirectContainer';

import { trackModule } from '../../../helpers/tracking/common/commonTrackingHelpers';
import findURL from '../../../gql/queries/findURL';
import { GRAPHQL_ENV } from '../../../gql';
import { getIsBot } from '../../../../state/ducks/App/App-Selectors';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getFeatureFlags } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { setPageInformation } from '../../../../state/ducks/App/App-Actions';
import {
    determineCollectionPrefixPath,
    determineProductPrefixPath,
    setCollectionPrefixPath,
    setProductPrefixPath,
} from '../../../helpers/determineInternationalizationPath';

const LoadableCoreBrowsingBuilder = loadable(() => import(/* webpackChunkName: "CoreBrowsingBuilder" */ './CoreBrowsingBuilder'));

/*
    Component Intent
        - This component's job is to take a path, vanity url, or bannercode and begin page determination
        - The priority for findURL is vanity > bannercode > path
        - When the findURL call is made we recieve a type back
        - That type will tell us what page to build and query to run next

        - There should most likely be 2 fallbacks:
            * if a pathname has no type returned, run a Search query
            * if no pathname, or a totally bogus one, revert to HomePage
*/

const CoreBrowsingContainer = ({
    location: { pathname, search }, brand, featureFlags, isBot = false, is404, productUnavailableName, pageInfo,
}) => {
    // after initial search findURL fails to run again
    // below if statement checks if 'searchterm' exists in url before calling find URL
    // if exists it will call search container
    if (pathname.includes('searchterm')) {
        const pathnameArr = pathname.split('/');
        const searchTerm = pathnameArr[2];
        return <GraphqlSearchContainer brand={brand} searchTerm={searchTerm} />;
    }

    // on page transition, bring user back to top of page - currently not working on back nav when cached
    useEffect(() => (typeof window !== 'undefined' ? window.scroll(0, 0) : null));
    const FIND_URL_QUERY = findURL(brand.domain, pathname);
    const { loading, data, error } = useQuery(FIND_URL_QUERY);

    if (loading) {
        return null;
    }

    if (error || !data) {
        mbpLogger.logError({
            query: FIND_URL_QUERY,
            component: 'CoreBrowsingContainer.js',
            message: 'Error loading data from Graphql',
            env: GRAPHQL_ENV,
            error,
        });
        return null;
    }

    if (featureFlags['is-internationalization-enabled']) {
        if (data?.findURL?.type === 'category') {
            return <Redirect to={determineCollectionPrefixPath(pathname, false)} />;
        }
        if (data?.findURL?.type === 'product') {
            return <Redirect to={determineProductPrefixPath(pathname, false)} />;
        }
    } else {
        if (data?.findURL?.type === 'category') {
            return <Redirect to={setCollectionPrefixPath(pathname)} />;
        }
        if (data?.findURL?.type === 'product') {
            return <Redirect to={setProductPrefixPath(pathname)} />;
        }
    }

    let pageData =  data.findURL;
    pageData = { path: pathname, search, ...pageData };

    if (mbpUtil.getEnv('REACT_APP_MBP_LOGGER_CONSOLE') === 'DEBUG') {
        console.log('CoreBrowsingContainer pageData', pageData);
    }
    pageInfo(pageData?.type);
    if (featureFlags['is-redirect-testing-enabled']
        && !isBot
        && pageData?.redirectTesting // TODO expand condition to account for vanity, bannercode when refactored - move flag down
    ) {
        mbpLogger.logDebug({ message: 'CoreBrowsingContainer ... redirecting' });

        return (
            <CoreBrowsingRedirectContainer
                page={pageData}
                featureFlags={featureFlags}
                brand={brand}
            />
        );
    }

    mbpLogger.logDebug({ message: `CoreBrowsingContainer ... sending to CoreBrowsingBuilder.  is404? ${is404}` });
    return (
        <LoadableCoreBrowsingBuilder
            brand={brand}
            page={pageData}
            is404={is404}
            productUnavailableName={productUnavailableName}
        />
    );
};

CoreBrowsingContainer.propTypes = {
    location: object.isRequired,
    brand: object.isRequired,
    featureFlags: object.isRequired,
    isBot: bool.isRequired,
    is404: bool,
    productUnavailableName: string,
    pageInfo: func.isRequired,
};

CoreBrowsingContainer.defaultProps = {
    is404: false,
    productUnavailableName: '',
};

const mapStateToProps = (state) => ({
    brand: getBrand(state),
    featureFlags: getFeatureFlags(state),
    isBot: getIsBot(state),
});

const mapDispatchToProps = (dispatch) => ({
    pageInfo: bindActionCreators(setPageInformation, dispatch),
});

const enhance = compose(
    trackModule('CoreBrowsing'),
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(CoreBrowsingContainer);
